import { Component, OnInit, ChangeDetectorRef, OnDestroy, ViewChild } from '@angular/core';
import { SiteService } from '../../services/context-discovery/site.service';
import { EventBusService } from '../../services/shared/event-bus.service';
import { CdtStatus } from '../../../cdt-status';
import { OrganisationSettingsService } from '../../services/organisation-settings.service';
import { PollingService } from '../../services/context-discovery/polling.service';
import { environment } from '../../../../environments/environment';
import { FeatureFlags } from '../../../../context-discovery-services/constants/feature-flags.const';
import { LaunchDarklyService } from '../../../../context-discovery-services/services/launch-darkly.service';

@Component({
  selector: 'adminapp-summary-wrapper',
  templateUrl: './summary-wrapper.component.html',
  styleUrls: ['./summary-wrapper.component.scss']
})
export class SummaryWrapperComponent implements OnInit, OnDestroy {

  isContextDiscoveryIsInProgress = false;
  showLoading = true;
  modelId = '';
  showNotFoundError: boolean;
  showAuthenticationError: boolean;
  showGenericError: boolean;
  launchDarklySubscription: any;
  isSiteData1UploadRequired: boolean;
  organizationId: string;
  latestSiteStatusResponse: string;
  showAssetIdentificationFailedError: boolean;
  isAssetIdentificationRequired: boolean;
  isModelPresent: boolean;
  organisationSettings = null;
  pollingService: PollingService;
  isUnifiedFlowEnabled: boolean;

  @ViewChild('summaryLayout') summaryLayout: any;

  constructor(private siteService: SiteService, private eventBus: EventBusService, private ref: ChangeDetectorRef,
    private organisationSettingsService: OrganisationSettingsService,
    private ldService: LaunchDarklyService) {
    this.organizationId = sessionStorage.getItem('organizationid');
    this.organisationSettingsService.setOrganisationId(this.organizationId);

    this.isUnifiedFlowEnabled = this.ldService.getBooleanFeatureFlagValue(FeatureFlags.UNIFIED_WORKFLOW);
    this.launchDarklySubscription = this.ldService.flagChange.subscribe(_ => {
      this.isUnifiedFlowEnabled = this.ldService.getBooleanFeatureFlagValue(FeatureFlags.UNIFIED_WORKFLOW, true);
    });

    this.eventBus.serverError$.subscribe((errorMessage) => {
      const summaryErrorToast: any = document.getElementById('summaryErrorToast');
      if (summaryErrorToast) {
        summaryErrorToast.text = errorMessage;
        summaryErrorToast.open();
      } else {
        console.error(errorMessage);
      }
    });
    this.eventBus.sameAppNavClickedSource$.subscribe((id: string) => {
      if (id === 'summary') {
        this.summaryLayout.retainNavSelection();
      }
    });
  }

  ngOnDestroy() {
    this.pollingService.stop();
  }

  ngOnInit() {
    this.initializeView();
    this.organisationSettings = null;

    this.pollingService = new PollingService(+environment.cdt.refreshInterval);
    this.pollingService.start(_ => this.siteService.getStatusFromOrganizationId(this.organizationId), statusResponse => {

      const statusResponseStringified = JSON.stringify(statusResponse);

      if (this.latestSiteStatusResponse !== statusResponseStringified) {
        this.latestSiteStatusResponse = statusResponseStringified;

        if (statusResponse) {

          this.removeAllErrors();
          this.resetStatusTrackers();

          switch (statusResponse.Status) {
            case CdtStatus.PUBLISH_IN_PROGRESS:
            case CdtStatus.PUBLISH_FAILED:
            case CdtStatus.COMPLETED: this.siteService.getModelId(this.organizationId).subscribe(modelIdResponse => {
              this.removeAllErrors();
              if (this.modelId !== modelIdResponse) {
                this.modelId = modelIdResponse;
              }
              this.checkSiteDataUploadRequiredAndShowView();
            }, this.handleFailure.bind(this));
              break;
            case CdtStatus.FAILED:
              this.showAssetIdentificationFailedError = true;
              this.showLoading = false;
              this.ref.detectChanges();
              break;
            case CdtStatus.IN_PROGRESS:
              this.showLoading = false;
              this.isContextDiscoveryIsInProgress = true;
              this.ref.detectChanges();
              break;
          }
        }
      }
    }, this.handleFailure.bind(this));
  }

  showPrerequisiteStepInModelAbsence() {
    this.organisationSettingsService.getSettings().subscribe(settings => {
      this.organisationSettings = settings;

      if (!settings.PreSales || !settings.PreSales.CdtStatus) {
        this.isSiteData1UploadRequired = true;
      } else if (settings.PreSales && settings.PreSales.CdtStatus === 'NewFile') {
        this.isAssetIdentificationRequired = true;
      }
      this.showLoading = false;
      this.ref.detectChanges();
    }, this.handleFailure.bind(this));
  }

  checkSiteDataUploadRequiredAndShowView() {
    if (this.modelId) {
      this.siteService.getModelPresenceStatus(this.modelId).subscribe(modelTtlExistenceStatus => {
        this.isModelPresent = modelTtlExistenceStatus;
        if (!modelTtlExistenceStatus) {
          this.showPrerequisiteStepInModelAbsence();
        } else {
          this.showLoading = false;
          this.ref.detectChanges();
        }
      }, this.handleFailure.bind(this));
    } else {
      this.showPrerequisiteStepInModelAbsence();
    }
  }

  removeAllErrors() {
    this.showGenericError = false;
    this.showAuthenticationError = false;
    this.showNotFoundError = false;
    this.showAssetIdentificationFailedError = false;
    this.showLoading = true;
  }

  resetStatusTrackers() {
    this.isContextDiscoveryIsInProgress = false;
    this.isSiteData1UploadRequired = false;
    this.isAssetIdentificationRequired = false;
    this.isModelPresent = false;
    this.showLoading = true;
  }

  redirectToUploadSiteDataView() {
    this.eventBus.conveyToNavigate('/upload');
  }

  handleFailure(errorResponse) {
    this.removeAllErrors();
    this.showLoading = false;
    switch (errorResponse.status) {
      case 401:
        this.showAuthenticationError = true;
        break;
      case 404:
        this.showNotFoundError = true;
        break;
      default:
        this.showGenericError = true;
    }
    this.ref.detectChanges();
  }

  canLayoutBeShown() {
    return !this.showLoading && this.modelId !== '' && !this.isContextDiscoveryIsInProgress && !this.isSiteData1UploadRequired
      && !this.isAnyErrorOccurred();
  }

  canLayoutBeShownForUnifiedWorkflow() {
    return this.canLayoutBeShown() && this.isUnifiedFlowEnabled;
  }

  isAnyErrorOccurred() {
    return this.showAuthenticationError || this.showNotFoundError || this.showGenericError || this.showAssetIdentificationFailedError;
  }

  retainAppSideNavSelection(id: string) {
    this.eventBus.conveyToRetainParentAppNavSelection(id);
  }

  initializeView() {
    this.removeAllErrors();
    this.resetStatusTrackers();
    this.modelId = '';
  }

  onGoToViewAssets() {
    this.eventBus.conveyToNavigate('/identify-assets');
  }
}
